import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { Card } from 'react-native-paper';
import { ShowToast } from '../Common/common';
import {
  FOEAuthorizationPendingItem,
  useApproveAuthorizationMutation,
  useGetPendingAuthorizationsQuery,
  useRejectAuthorizationMutation,
} from '../redux/services/foauthorizationApi';
import {
  isSoundAlertEnabled,
  playSoundAlert,
  setSoundAlertEnabled,
} from '../utils/soundAlert';
import { colors, borderRadius, shadows, spacing, typography } from '../theme/theme';
import {
  APPROVAL_PRESETS,
  REJECTION_PRESETS,
  formatAuthType,
  formatAuthValue,
  getAuthTypeColor,
} from '../utils/pendingAuthUtils';

import { usePhoneticText } from '../utils/transliterate/usePhoneticInput';
import T from '../Components/T';
import { tp } from '../utils/translateText';
const PendingAuthorizations: React.FC<UniversalProps> = ({ navigation }) => {
  const { data: authorizations = [], isLoading, refetch, isFetching } =
    useGetPendingAuthorizationsQuery();

  const [approveAuth, { isLoading: isApproving }] = useApproveAuthorizationMutation();
  const [rejectAuth, { isLoading: isRejecting }] = useRejectAuthorizationMutation();

  const ph = usePhoneticText();
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [activeModal, setActiveModal] = useState<{
    visible: boolean;
    action: 'approve' | 'reject' | null;
    targetAuth: FOEAuthorizationPendingItem | null;
    remarks: string;
  }>({
    visible: false,
    action: null,
    targetAuth: null,
    remarks: '',
  });

  // Load sound alert setting
  useEffect(() => {
    (async () => {
      const enabled = await isSoundAlertEnabled();
      setSoundEnabled(enabled);
    })();
  }, []);

  const handleToggleSound = async () => {
    const nextVal = !soundEnabled;
    setSoundEnabled(nextVal);
    await setSoundAlertEnabled(nextVal);
    if (nextVal) {
      // Preview / test chime
      await playSoundAlert(true);
      ShowToast('Sound alerts enabled', 'success');
    } else {
      ShowToast('Sound alerts disabled', 'info');
    }
  };

  const openApproveModal = (auth: FOEAuthorizationPendingItem) => {
    setActiveModal({
      visible: true,
      action: 'approve',
      targetAuth: auth,
      remarks: APPROVAL_PRESETS[0],
    });
  };

  const openRejectModal = (auth: FOEAuthorizationPendingItem) => {
    setActiveModal({
      visible: true,
      action: 'reject',
      targetAuth: auth,
      remarks: REJECTION_PRESETS[0],
    });
  };

  const handleConfirmAction = async () => {
    if (!activeModal.targetAuth || !activeModal.action) return;
    const { targetAuth, action, remarks } = activeModal;

    try {
      if (action === 'approve') {
        await approveAuth({
          au_id: targetAuth.au_id,
          remarks: remarks.trim() || 'Approved by Manager',
        }).unwrap();
        ShowToast(
          `Approved Room ${targetAuth.rm_name} authorization`,
          'success'
        );
      } else {
        await rejectAuth({
          au_id: targetAuth.au_id,
          remarks: remarks.trim() || 'Rejected: Not approved',
        }).unwrap();
        ShowToast(
          `Rejected Room ${targetAuth.rm_name} authorization`,
          'info'
        );
      }
      setActiveModal({
        visible: false,
        action: null,
        targetAuth: null,
        remarks: '',
      });
    } catch (err: any) {
      ShowToast(
        err?.data?.message || err?.message || `Failed to ${action} authorization`,
        'error'
      );
    }
  };

  const isSubmitting = isApproving || isRejecting;
  const pendingCount = authorizations.length;

  const renderItem = ({ item }: { item: FOEAuthorizationPendingItem }) => {
    const typeColor = getAuthTypeColor(item.au_type);
    const typeLabel = formatAuthType(item.au_type);
    const valueLabel = formatAuthValue(item);
    const formattedDate = moment(item.au_request_date).format('DD-MM-YY HH:mm');

    return (
      <Card style={styles.card} elevation={3}>
        {/* Card Header */}
        <View style={styles.cardHeader}>
          <View style={styles.roomTag}>
            <FontAwesome name="bed" size={14} color="#fff" style={{ marginRight: 6 }} />
            <Text style={styles.roomTagText}><T>Room</T> {item.rm_name}</Text>
          </View>
          <View style={[styles.typeBadge, { backgroundColor: typeColor.bg }]}>
            <Text style={[styles.typeBadgeText, { color: typeColor.text }]}>
              {typeLabel}
            </Text>
          </View>
        </View>

        {/* Card Body */}
        <View style={styles.cardBody}>
          <View style={styles.infoRow}>
            <FontAwesome name="user" size={15} color={colors.primary} style={styles.infoIcon} />
            <Text style={styles.infoLabel}><T>Guest:</T></Text>
            <Text style={styles.infoValue}>{item.guest_name || 'N/A'}</Text>
          </View>

          <View style={styles.infoRow}>
            <FontAwesome name="tag" size={15} color="#3B82F6" style={styles.infoIcon} />
            <Text style={styles.infoLabel}><T>Value:</T></Text>
            <Text style={[styles.infoValue, styles.boldValue]}>{valueLabel}</Text>
          </View>

          <View style={styles.infoRow}>
            <FontAwesome name="user-circle" size={15} color="#6B7280" style={styles.infoIcon} />
            <Text style={styles.infoLabel}><T>Requested by:</T></Text>
            <Text style={styles.infoValue}>
              {item.requester_name || item.au_requester}
            </Text>
          </View>

          <View style={styles.infoRow}>
            <FontAwesome name="clock-o" size={15} color="#9CA3AF" style={styles.infoIcon} />
            <Text style={styles.infoLabel}><T>Requested at:</T></Text>
            <Text style={styles.infoValue}>{formattedDate}</Text>
          </View>

          {Boolean(item.au_remarks) && (
            <View style={styles.remarksBox}>
              <FontAwesome name="comment-o" size={13} color="#6B7280" style={{ marginRight: 6, marginTop: 2 }} />
              <Text style={styles.remarksText}>{item.au_remarks}</Text>
            </View>
          )}
        </View>

        {/* Card Actions */}
        <View style={styles.cardFooter}>
          <TouchableOpacity
            style={[styles.actionBtn, styles.approveBtn]}
            activeOpacity={0.8}
            onPress={() => openApproveModal(item)}
            disabled={isSubmitting}>
            <FontAwesome name="check" size={14} color="#fff" style={{ marginRight: 6 }} />
            <Text style={styles.actionBtnText}><T>Approve</T></Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.actionBtn, styles.rejectBtn]}
            activeOpacity={0.8}
            onPress={() => openRejectModal(item)}
            disabled={isSubmitting}>
            <FontAwesome name="times" size={14} color="#fff" style={{ marginRight: 6 }} />
            <Text style={styles.actionBtnText}><T>Reject</T></Text>
          </TouchableOpacity>
        </View>
      </Card>
    );
  };

  return (
    <View style={styles.container}>
      {/* Top Header Bar */}
      <View style={styles.toolbar}>
        <View style={styles.titleRow}>
          <Text style={styles.screenTitle}><T>Pending Authorizations</T></Text>
          <View style={[styles.countBadge, pendingCount > 0 ? styles.countActive : styles.countZero]}>
            <Text style={styles.countBadgeText}>{pendingCount}</Text>
          </View>
        </View>

        <View style={styles.toolbarActions}>
          {/* Sound Alert Toggle */}
          <TouchableOpacity
            style={[
              styles.soundToggleBtn,
              soundEnabled ? styles.soundActive : styles.soundMuted,
            ]}
            onPress={handleToggleSound}
            activeOpacity={0.8}>
            <FontAwesome
              name={soundEnabled ? 'volume-up' : 'volume-off'}
              size={15}
              color={soundEnabled ? '#fff' : '#6B7280'}
              style={{ marginRight: 6 }}
            />
            <Text
              style={[
                styles.soundToggleText,
                { color: soundEnabled ? '#fff' : '#4B5563' },
              ]}>
              {soundEnabled ? 'Sound: ON' : 'Sound: OFF'}
            </Text>
          </TouchableOpacity>

          {/* Refresh Button */}
          <TouchableOpacity
            style={styles.refreshBtn}
            onPress={() => refetch()}
            disabled={isFetching}
            activeOpacity={0.7}>
            <FontAwesome
              name="refresh"
              size={15}
              color={colors.primary}
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Main List */}
      {isLoading ? (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}><T>Loading authorization requests...</T></Text>
        </View>
      ) : pendingCount === 0 ? (
        <ScrollView
          contentContainerStyle={styles.centerContainer}
          refreshControl={
            <RefreshControl refreshing={isFetching} onRefresh={refetch} />
          }>
          <View style={styles.emptyIconCircle}>
            <FontAwesome name="shield" size={48} color="#03C988" />
          </View>
          <Text style={styles.emptyTitle}><T>No Pending Authorizations</T></Text>
          <Text style={styles.emptySubtitle}>
            <T>All front-office authorization requests have been resolved.</T>
          </Text>
        </ScrollView>
      ) : (
        <FlatList
          data={authorizations}
          keyExtractor={item => item.au_id.toString()}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={isFetching} onRefresh={refetch} />
          }
        />
      )}

      {/* Approval / Rejection Modal Dialog */}
      <Modal
        visible={activeModal.visible}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (!isSubmitting) {
            setActiveModal({
              visible: false,
              action: null,
              targetAuth: null,
              remarks: '',
            });
          }
        }}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text
                style={[
                  styles.modalTitle,
                  {
                    color:
                      activeModal.action === 'approve' ? '#03C988' : '#EF4444',
                  },
                ]}>
                {activeModal.action === 'approve'
                  ? 'Approve Authorization'
                  : 'Reject Authorization'}
              </Text>
              <Text style={styles.modalSub}>
                <T>Room</T> {activeModal.targetAuth?.rm_name} -{' '}
                {formatAuthType(activeModal.targetAuth?.au_type || '')}
              </Text>
            </View>

            {/* Quick Remarks Presets */}
            <Text style={styles.presetsLabel}><T>Quick Remarks:</T></Text>
            <View style={styles.presetsWrap}>
              {(activeModal.action === 'approve'
                ? APPROVAL_PRESETS
                : REJECTION_PRESETS
              ).map((preset, idx) => (
                <TouchableOpacity
                  key={idx}
                  style={[
                    styles.presetChip,
                    activeModal.remarks === preset && styles.presetChipActive,
                  ]}
                  onPress={() =>
                    setActiveModal(prev => ({ ...prev, remarks: preset }))
                  }>
                  <Text
                    style={[
                      styles.presetChipText,
                      activeModal.remarks === preset && styles.presetChipTextActive,
                    ]}>
                    {preset}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Remarks Input */}
            <Text style={styles.inputLabel}><T>Remarks / Reason:</T></Text>
            <TextInput
              style={styles.remarksInput}
              value={activeModal.remarks}
              onChangeText={text =>
                setActiveModal(prev => ({ ...prev, remarks: ph.onChange(text) }))
              }
              onBlur={() =>
                setActiveModal(prev => ({ ...prev, remarks: ph.onBlur(prev.remarks) }))
              }
              placeholder={tp("Enter remarks...")}
              placeholderTextColor="#9CA3AF"
              multiline
              numberOfLines={3}
            />

            {/* Modal Actions */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalCancelBtn}
                onPress={() =>
                  setActiveModal({
                    visible: false,
                    action: null,
                    targetAuth: null,
                    remarks: '',
                  })
                }
                disabled={isSubmitting}>
                <Text style={styles.modalCancelText}><T>Cancel</T></Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.modalSubmitBtn,
                  {
                    backgroundColor:
                      activeModal.action === 'approve' ? '#03C988' : '#EF4444',
                  },
                ]}
                onPress={handleConfirmAction}
                disabled={isSubmitting}>
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.modalSubmitText}>
                    {activeModal.action === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderColor: '#E5E7EB',
    ...shadows.sm,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  screenTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1F2937',
    marginRight: 8,
  },
  countBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    minWidth: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countActive: {
    backgroundColor: '#EF4444',
  },
  countZero: {
    backgroundColor: '#9CA3AF',
  },
  countBadgeText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 12,
  },
  toolbarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  soundToggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: borderRadius.md,
  },
  soundActive: {
    backgroundColor: '#03C988',
  },
  soundMuted: {
    backgroundColor: '#E5E7EB',
  },
  soundToggleText: {
    fontSize: 12,
    fontWeight: '600',
  },
  refreshBtn: {
    padding: 8,
    borderRadius: borderRadius.md,
    backgroundColor: '#F3F4F6',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  loadingText: {
    marginTop: 12,
    color: '#6B7280',
    fontSize: 14,
  },
  emptyIconCircle: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: 'rgba(3, 201, 136, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 6,
  },
  emptySubtitle: {
    fontSize: 13,
    color: '#6B7280',
    textAlign: 'center',
  },
  listContent: {
    padding: 12,
    paddingBottom: 80,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: borderRadius.lg,
    marginBottom: 12,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderColor: '#F3F4F6',
    backgroundColor: '#FAFAFA',
  },
  roomTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  roomTagText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 13,
  },
  typeBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  typeBadgeText: {
    fontWeight: '700',
    fontSize: 12,
  },
  cardBody: {
    padding: 14,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  infoIcon: {
    width: 20,
    marginRight: 8,
  },
  infoLabel: {
    width: 105,
    fontSize: 13,
    color: '#6B7280',
    fontWeight: '500',
  },
  infoValue: {
    flex: 1,
    fontSize: 13,
    color: '#1F2937',
  },
  boldValue: {
    fontWeight: '700',
    color: '#1D4ED8',
  },
  remarksBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
    padding: 8,
    marginTop: 6,
    borderLeftWidth: 3,
    borderColor: '#3B82F6',
  },
  remarksText: {
    flex: 1,
    fontSize: 12,
    color: '#4B5563',
    fontStyle: 'italic',
  },
  cardFooter: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: '#FAFAFA',
    borderTopWidth: 1,
    borderColor: '#F3F4F6',
    gap: 10,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 10,
    borderRadius: borderRadius.md,
  },
  approveBtn: {
    backgroundColor: '#03C988',
  },
  rejectBtn: {
    backgroundColor: '#FF2171',
  },
  actionBtnText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 13,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: '#FFFFFF',
    borderRadius: borderRadius.xl,
    padding: 20,
    ...shadows.xl,
  },
  modalHeader: {
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  modalSub: {
    fontSize: 13,
    color: '#6B7280',
  },
  presetsLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4B5563',
    marginBottom: 8,
  },
  presetsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 14,
  },
  presetChip: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  presetChipActive: {
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
    borderColor: '#3B82F6',
  },
  presetChipText: {
    fontSize: 11,
    color: '#4B5563',
    fontWeight: '500',
  },
  presetChipTextActive: {
    color: '#1D4ED8',
    fontWeight: '700',
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4B5563',
    marginBottom: 6,
  },
  remarksInput: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: borderRadius.md,
    padding: 10,
    fontSize: 13,
    color: '#1F2937',
    textAlignVertical: 'top',
    minHeight: 70,
    marginBottom: 16,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  modalCancelBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: borderRadius.md,
    backgroundColor: '#F3F4F6',
  },
  modalCancelText: {
    color: '#4B5563',
    fontWeight: '600',
    fontSize: 13,
  },
  modalSubmitBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: borderRadius.md,
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalSubmitText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 13,
  },
});

export default PendingAuthorizations;
